import { FC } from 'react';
import { BrandLogo, Checkbox, Icon, Tooltip } from '@kontentino/ui';
import { faArrowsRotate } from '@fortawesome/pro-regular-svg-icons/faArrowsRotate';
import clsx from 'clsx';
import getSocialMediaTypeForBrandLogo from 'app/utils/getSocialMediaTypeForBrandLogo';
import { getYtLogoGuidelineSize } from 'app/utils/getytlogoguidelinesize';
import { useAnalyticsSyncStore } from 'app/modules/analytics/components/AnalyticsSyncDriver';
import type { PageData, ProfileGroupPage } from 'types/Page';

const RowSyncIndicator: FC = () => (
  <Icon
    icon={faArrowsRotate}
    size="sm"
    className="tw-ml-auto tw-shrink-0 tw-animate-spin tw-text-primary-100"
    data-cy="analytics_sidebar_profile-item_syncing"
  />
);

type SelectableProps = {
  variant: 'selectable';
  page: ProfileGroupPage;
  checked: boolean;
  onToggle: () => void;
  isDisabled?: boolean;
  disabledTooltip?: string;
};

type NavigableProps = {
  variant: 'navigable';
  page: PageData;
  isActive: boolean;
  onSelect: () => void;
};

export type ProfileListRowProps = SelectableProps | NavigableProps;

const rowBaseClass =
  'tw-flex tw-h-8 tw-w-full tw-items-center tw-gap-2 tw-rounded tw-px-2 tw-py-[6px] tw-text-left';

const rowInteractiveClass =
  'tw-cursor-pointer hover:tw-bg-primary-10 hover:tw-text-primary-100';

const ProfileListRow: FC<ProfileListRowProps> = (props) => {
  const isRowSyncing = useAnalyticsSyncStore(
    (state) => state.isSyncing && state.syncingPageIds.includes(props.page.id),
  );

  if (props.variant === 'selectable') {
    const { page, checked, onToggle, isDisabled, disabledTooltip } = props;
    const row = (
      <div
        role="button"
        tabIndex={isDisabled ? -1 : 0}
        aria-disabled={isDisabled}
        onClick={() => {
          if (!isDisabled) {
            onToggle();
          }
        }}
        onKeyDown={(e) => {
          if (isDisabled) {
            return;
          }
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
        className={clsx(rowBaseClass, {
          [rowInteractiveClass]: !isDisabled,
          'tw-cursor-not-allowed tw-text-grayscale-70': isDisabled,
        })}
        data-cy="analytics_sidebar_profile-item"
      >
        <span className="tw-pointer-events-none">
          <Checkbox
            size="small"
            checked={checked}
            disabled={isDisabled}
            onChange={() => {}}
            readOnly
          />
        </span>
        <BrandLogo
          size={20}
          iconSize={getYtLogoGuidelineSize(10, page.type)}
          name={page.name}
          socialMediaType={getSocialMediaTypeForBrandLogo(page.type)}
        />
        <span
          translate="no"
          className="tw-truncate tw-text-sm tw-font-medium tw-text-inherit"
        >
          {page.name}
        </span>
        {isRowSyncing && <RowSyncIndicator />}
      </div>
    );

    if (isDisabled && disabledTooltip) {
      return (
        <Tooltip content={disabledTooltip}>
          <span className="tw-inline-flex tw-w-full">{row}</span>
        </Tooltip>
      );
    }

    return row;
  }

  const { page, isActive, onSelect } = props;
  return (
    <button
      type="button"
      onClick={onSelect}
      className={clsx(
        rowBaseClass,
        rowInteractiveClass,
        'tw-text-grayscale-180',
        {
          'tw-bg-primary-10': isActive,
        },
      )}
      data-cy="analytics_sidebar_profile-item"
    >
      <BrandLogo
        size={20}
        iconSize={getYtLogoGuidelineSize(10, page.type)}
        name={page.name}
        src={page.logo?.src ?? undefined}
        socialMediaType={getSocialMediaTypeForBrandLogo(page.type)}
      />
      <span
        translate="no"
        className="tw-truncate tw-text-md tw-font-medium tw-text-inherit"
      >
        {page.name}
      </span>
      {isRowSyncing && <RowSyncIndicator />}
    </button>
  );
};

export default ProfileListRow;
